import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '../design/Avatar';
import EmptyState from '../design/EmptyState';
import Icon from '../design/Icon';
import StatusChip from '../design/StatusChip';
import { statusFromScore } from '../utils/pasuk';
import type { Student, StudentProgress } from '../types';

interface StudentsTableProps {
  students: Student[];
  progressByStudent: Record<string, StudentProgress>;
  /** pasukKey() of the class's current pasuk, or null if none set. */
  currentKey: string | null;
  onSelect: (student: Student) => void;
}

const StudentsTable: React.FC<StudentsTableProps> = ({
  students,
  progressByStudent,
  currentKey,
  onSelect,
}) => {
  const { t, i18n } = useTranslation();

  if (students.length === 0) {
    return (
      <EmptyState
        icon="users"
        title={t('school_no_students')}
        description={t('school_no_students_hint')}
      />
    );
  }

  const formatDate = (progress: StudentProgress | undefined): string =>
    progress?.lastActivityAt
      ? progress.lastActivityAt
          .toDate()
          .toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
      : '—';

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-school-line text-start text-xs font-semibold text-school-muted">
            <th className="px-5 py-3 text-start">{t('school_col_student')}</th>
            <th className="px-3 py-3 text-start">{t('school_col_code')}</th>
            <th className="px-3 py-3 text-start">{t('school_col_status')}</th>
            <th className="px-3 py-3 text-start">{t('school_col_attempts')}</th>
            <th className="px-3 py-3 text-start">{t('school_col_best')}</th>
            <th className="px-3 py-3 text-start">{t('school_col_last_activity')}</th>
            <th className="px-3 py-3" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {students.map((s) => {
            const progress = progressByStudent[s.id];
            const current = currentKey ? progress?.pesukim[currentKey] : undefined;
            const name = `${s.firstName} ${s.lastName}`;
            return (
              <tr
                key={s.id}
                onClick={(): void => onSelect(s)}
                className="cursor-pointer border-b border-school-line last:border-0 hover:bg-school-canvas"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={name} size="sm" />
                    <span className="font-semibold text-school-ink">{name}</span>
                  </div>
                </td>
                <td className="px-3 py-3 font-mono text-school-muted" dir="ltr">
                  {s.loginCode}
                </td>
                <td className="px-3 py-3">
                  <StatusChip
                    status={statusFromScore(current?.bestScore, current?.attempts ?? 0)}
                  />
                </td>
                <td className="px-3 py-3 tabular-nums text-school-ink">{current?.attempts ?? 0}</td>
                <td className="px-3 py-3 tabular-nums text-school-ink">
                  {current ? current.bestScore : '—'}
                </td>
                <td className="px-3 py-3 text-school-muted">{formatDate(progress)}</td>
                <td className="px-3 py-3 text-school-neutral">
                  <Icon name="chevron" className="h-4 w-4" flipRtl />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StudentsTable;
